import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/shared/api/api-client';
import { refusalMessage } from '../ambient/decision-identity';
import type {
  AcknowledgeAlertBody,
  AmbientAlertAcknowledgementPort,
  AmbientAlertAcknowledgementRow,
} from '../ambient/alert-acknowledgement-types';

// PROD-2598 — the Scrips.Patient ambient clinical-alert acknowledgement endpoints:
//
//   POST api/Patients/{patientId}/Encounters/{encounterId}/AmbientAlertAcknowledgements
//   GET  api/Patients/{patientId}/Encounters/{encounterId}/AmbientAlertAcknowledgements
//
// An AUDIT record (shown + acknowledged, by whom, when), not part of the clinical record. Errors
// are NOT caught here: they propagate to the panel, which renders them. A write that succeeds
// refreshes the list, so what the panel shows is the server's record.

const path = (patientId: string, encounterId: string) =>
  `/api/Patients/${encodeURIComponent(patientId)}/Encounters/${encodeURIComponent(encounterId)}/AmbientAlertAcknowledgements`;

export const ambientAlertAcknowledgementsKey = (patientId?: string | null, encounterId?: string | null) =>
  ['ambient-alert-acknowledgements', patientId, encounterId] as const;

/**
 * The port the ambient panel records acknowledgements through. `undefined` until the patient and the
 * encounter are both known — the panel then keeps acknowledgements in memory for the session only.
 */
export function useAmbientAlertAcknowledgementPort(
  patientId: string | null | undefined,
  encounterId: string | null | undefined,
): AmbientAlertAcknowledgementPort | undefined {
  const queryClient = useQueryClient();
  const query = useQuery<AmbientAlertAcknowledgementRow[]>({
    queryKey: ambientAlertAcknowledgementsKey(patientId, encounterId),
    enabled: !!patientId && !!encounterId,
    queryFn: async ({ signal }) =>
      (await get<AmbientAlertAcknowledgementRow[]>(path(patientId as string, encounterId as string), { signal })) ?? [],
  });
  const data = query.data;
  const readError = query.error ? refusalMessage(query.error) : null;

  return useMemo(() => {
    if (!patientId || !encounterId) return undefined;
    return {
      acknowledgements: data,
      readError,
      acknowledge: async (body: AcknowledgeAlertBody) => {
        const row = await post<AmbientAlertAcknowledgementRow>(path(patientId, encounterId), body);
        void queryClient.invalidateQueries({ queryKey: ambientAlertAcknowledgementsKey(patientId, encounterId) });
        return row;
      },
    };
  }, [patientId, encounterId, data, readError, queryClient]);
}
